import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase F3 — standalone ACH authorization contract creation. No rental
 * required, single ACH document on the contract. Subsequent signing uses the
 * F2 bank-info + ACH-signature flow (minus the rental steps).
 */
export function CreateAchContractDialog({ open, onOpenChange, customerId, customerName, onCreated }) {
    const [title, setTitle] = useState("");
    const [notes, setNotes] = useState("");
    const [templateId, setTemplateId] = useState("");
    const [templates, setTemplates] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTitle(""); setNotes(""); setTemplateId("");
        (async () => {
            try {
                const { data } = await api.get("/admin/templates", { params: { limit: 100, template_type: "ach_authorization" } });
                const items = (data.items || []).filter((t) => !t.archived);
                setTemplates(items);
                // Default to the system default if exactly one ACH template
                const sysDefault = items.find((t) => t.is_system_default);
                if (sysDefault) setTemplateId(sysDefault.id);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
    }, [open]);

    const submit = async () => {
        if (!title.trim()) { toast.error("Title is required"); return; }
        if (!templateId) { toast.error("Pick an ACH template"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/customers/${customerId}/contracts`, {
                type: "ach_authorization",
                title: title.trim(),
                template_id: templateId,
                notes: notes.trim() || undefined,
            });
            toast.success("ACH authorization created");
            onOpenChange(false);
            onCreated?.(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="create-ach-contract-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">Create ACH authorization</DialogTitle>
                    <DialogDescription>
                        Standalone ACH authorization for {customerName || "this customer"} — no rental required.
                        The customer captures their bank info and signs in a single session.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label>ACH template *</Label>
                        <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                            data-testid="ach-template-select">
                            <option value="">— pick a template —</option>
                            {templates.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}{t.is_system_default ? "  ★" : ""}</option>
                            ))}
                        </select>
                        {templates.length === 0 && (
                            <p className="text-xs text-amber-700">No ACH templates yet. The system default is seeded automatically — if you don't see it, contact your account manager.</p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <Label>Title *</Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. ACH Authorization — June 2026" data-testid="ach-contract-title" />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Internal notes</Label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                            className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                            data-testid="ach-contract-notes" />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="create-ach-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-blue-900 hover:bg-blue-800"
                        data-testid="create-ach-submit">
                        {busy ? "Creating…" : "Create ACH authorization"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CreateAchContractDialog;
